import axios from 'axios';
import { ApiPlayer, Position } from '../types';
import { PLAYER_IMAGE_API } from '../config/api.config';
import { getCachedImage, cacheImage } from './imageCache';

// Advanced search filters interface
interface AdvancedSearchFilters {
  year?: number;
  country?: string;
  league?: string;
  team?: string;
}

// TheSportsDB API v1 Base URL and endpoints
const THESPORTSDB_BASE_URL = 'https://www.thesportsdb.com/api/v1/json/123';

// TheSportsDB API response interfaces
interface TheSportsDBTeamSearchResponse {
  teams?: Array<{
    idTeam: string;
    strTeam: string;
    strSport?: string;
    strCountry?: string;
  }>;
}

interface TheSportsDBPlayerSearchResponse {
  player?: Array<{
    idPlayer: string;
    strPlayer: string;
    strPosition?: string;
    strSport?: string;
    strNationality?: string;
    strBirthLocation?: string;
    strTeam?: string;
    strCutout?: string;
    strThumb?: string;
  }>;
}

interface TheSportsDBLeagueSearchResponse {
  countrys?: Array<{
    strLeague: string;
    strCountry: string;
  }>;
}

interface TheSportsDBTeamsInLeagueResponse {
  teams?: Array<{
    idTeam: string;
    strTeam: string;
  }>;
}

// Enhanced function to search SOCCER players using multiple TheSportsDB endpoints
export const searchPlayers = async (
  query: string, 
  position?: Position,
  advancedFilters?: AdvancedSearchFilters
): Promise<ApiPlayer[]> => {
  console.log(`🔍 Starting enhanced SOCCER search with:
    query: "${query}"
    position: ${position || 'any'}
    filters:`, advancedFilters);

  // Always start with mock data for immediate response
  const mockResults = mockSearchResults(query, position, advancedFilters);
  
  // If no query and no team filter, return mock data immediately
  if (!query.trim() && !advancedFilters?.team) {
    console.log('📝 No search query or team filter - returning mock data only');
    return mockResults;
  }

  try {
    let allPlayers: ApiPlayer[] = [];

    // Strategy 1: If we have a team filter, search by team first
    if (advancedFilters?.team) {
      console.log(`🏟️ Searching for teams matching: "${advancedFilters.team}"`);
      const teamPlayers = await searchPlayersByTeam(advancedFilters.team, query, position);
      allPlayers = [...allPlayers, ...teamPlayers];
    }

    // Strategy 2: Direct player name search
    if (query.trim()) {
      console.log(`👤 Direct player search for: "${query}"`);
      const directPlayers = await searchPlayersByName(query, position);
      
      // Merge without duplicates
      const existingIds = new Set(allPlayers.map(p => p.id));
      const newPlayers = directPlayers.filter(p => !existingIds.has(p.id));
      allPlayers = [...allPlayers, ...newPlayers];
    }

    // Strategy 3: If we have league filter, search by league
    if (advancedFilters?.league) {
      console.log(`🏆 Searching by league: "${advancedFilters.league}"`);
      const leaguePlayers = await searchPlayersByLeague(advancedFilters.league, query, position);
      
      // Merge without duplicates
      const existingIds = new Set(allPlayers.map(p => p.id));
      const newPlayers = leaguePlayers.filter(p => !existingIds.has(p.id));
      allPlayers = [...allPlayers, ...newPlayers];
    }

    // Apply remaining filters
    let filteredPlayers = allPlayers;
    
    if (advancedFilters) {
      filteredPlayers = applyAdvancedFilters(allPlayers, advancedFilters);
    }

    console.log(`✅ Found ${filteredPlayers.length} soccer players from API`);

    // If we got good results from API, combine with a few mock players
    if (filteredPlayers.length > 0) {
      const popularMockPlayers = mockResults.slice(0, 3); // Add 3 popular mock players
      const combinedResults = [...filteredPlayers, ...popularMockPlayers];
      return sortPlayersByPositionPriority(combinedResults, position);
    } else {
      console.log('⚠️ No API results found, falling back to mock data');
      return mockResults;
    }

  } catch (error) {
    console.error('❌ Error in enhanced soccer search:', error);
    return mockResults;
  }
};

// Search players by team name using TheSportsDB team search + team players endpoint
const searchPlayersByTeam = async (teamName: string, playerQuery?: string, position?: Position): Promise<ApiPlayer[]> => {
  try {
    // Step 1: Search for teams
    const teamSearchUrl = `${THESPORTSDB_BASE_URL}/searchteams.php`;
    const teamResponse = await axios.get(teamSearchUrl, {
      params: { t: teamName },
      timeout: 5000
    });

    const teamData = teamResponse.data as TheSportsDBTeamSearchResponse;
    if (!teamData?.teams) {
      console.log('No teams found for:', teamName);
      return [];
    }

    const allPlayers: ApiPlayer[] = [];

    // Step 2: Get players for each matching team (limit to first 3 teams)
    const teams = teamData.teams.slice(0, 3);
    
    for (const team of teams) {
      // Only process soccer teams
      if (team.strSport?.toLowerCase() !== 'soccer') {
        console.log(`Skipping non-soccer team: ${team.strTeam} (${team.strSport})`);
        continue;
      }

      console.log(`🔍 Getting players for soccer team: ${team.strTeam} (${team.strCountry})`);
      
      try {
        const playersUrl = `${THESPORTSDB_BASE_URL}/lookup_all_players.php`;
        const playersResponse = await axios.get(playersUrl, {
          params: { id: team.idTeam },
          timeout: 5000
        });

        const playerData = playersResponse.data as TheSportsDBPlayerSearchResponse;
        if (playerData?.player) {
          const teamPlayers = parseTheSportsDBPlayers(
            playerData.player, 
            position,
            playerQuery,
            { teamName: team.strTeam, teamCountry: team.strCountry }
          );
          allPlayers.push(...teamPlayers);
        }
      } catch (playerError) {
        console.error(`Error getting players for team ${team.strTeam}:`, playerError);
      }
    }

    return allPlayers;

  } catch (error) {
    console.error('Error searching players by team:', error);
    return [];
  }
};

// Search players by name using TheSportsDB player search endpoint
const searchPlayersByName = async (playerName: string, position?: Position): Promise<ApiPlayer[]> => {
  try {
    const searchUrl = `${THESPORTSDB_BASE_URL}/searchplayers.php`;
    const response = await axios.get(searchUrl, {
      params: { p: playerName },
      timeout: 5000
    });

    const playerData = response.data as TheSportsDBPlayerSearchResponse;
    if (!playerData?.player) {
      console.log('No players found for name:', playerName);
      return [];
    }

    return parseTheSportsDBPlayers(playerData.player, position, playerName);

  } catch (error) {
    console.error('Error searching players by name:', error);
    return [];
  }
};

// Search players by league using league teams + team players endpoints
const searchPlayersByLeague = async (leagueName: string, playerQuery?: string, position?: Position): Promise<ApiPlayer[]> => {
  try {
    // Search for leagues
    const leagueSearchUrl = `${THESPORTSDB_BASE_URL}/search_all_leagues.php`;
    const leagueResponse = await axios.get(leagueSearchUrl, {
      params: { 
        c: '', // country - we'll filter by league name instead
        s: 'Soccer' // ensure we only get soccer leagues
      },
      timeout: 5000
    });

    const leagueData = leagueResponse.data as TheSportsDBLeagueSearchResponse;
    if (!leagueData?.countrys) {
      console.log('No leagues found');
      return [];
    }

    // Find matching league
    const matchingLeague = leagueData.countrys.find((league: any) => 
      league.strLeague?.toLowerCase().includes(leagueName.toLowerCase()) ||
      leagueName.toLowerCase().includes(league.strLeague?.toLowerCase())
    );

    if (!matchingLeague) {
      console.log('No matching league found for:', leagueName);
      return [];
    }

    console.log(`🏆 Found league: ${matchingLeague.strLeague} (${matchingLeague.strCountry})`);

    // Get teams in the league
    const teamsUrl = `${THESPORTSDB_BASE_URL}/search_all_teams.php`;
    const teamsResponse = await axios.get(teamsUrl, {
      params: { l: matchingLeague.strLeague },
      timeout: 5000
    });

    const teamsData = teamsResponse.data as TheSportsDBTeamsInLeagueResponse;
    if (!teamsData?.teams) {
      console.log('No teams found in league:', matchingLeague.strLeague);
      return [];
    }

    const allPlayers: ApiPlayer[] = [];
    const teams = teamsData.teams.slice(0, 5); // Limit to 5 teams to avoid too many requests

    for (const team of teams) {
      try {
        const playersUrl = `${THESPORTSDB_BASE_URL}/lookup_all_players.php`;
        const playersResponse = await axios.get(playersUrl, {
          params: { id: team.idTeam },
          timeout: 3000
        });

        const playerData = playersResponse.data as TheSportsDBPlayerSearchResponse;
        if (playerData?.player) {
          const teamPlayers = parseTheSportsDBPlayers(
            playerData.player, 
            position,
            playerQuery,
            { teamName: team.strTeam, leagueName: matchingLeague.strLeague }
          );
          allPlayers.push(...teamPlayers);
        }
      } catch (playerError) {
        console.error(`Error getting players for team ${team.strTeam}:`, playerError);
      }
    }

    return allPlayers;

  } catch (error) {
    console.error('Error searching players by league:', error);
    return [];
  }
};

// Enhanced function to parse TheSportsDB player data with better filtering
const parseTheSportsDBPlayers = (
  playerData: any[], 
  positionFilter?: Position,
  nameQuery?: string,
  teamInfo?: { teamName?: string; teamCountry?: string; leagueName?: string }
): ApiPlayer[] => {
  if (!playerData || !Array.isArray(playerData)) {
    return [];
  }

  const players: ApiPlayer[] = [];

  for (const player of playerData) {
    // CRITICAL: Only include soccer players
    if (player.strSport?.toLowerCase() !== 'soccer' && player.strSport?.toLowerCase() !== 'football') {
      continue;
    }

    // Filter by name if provided
    if (nameQuery && player.strPlayer) {
      const playerName = player.strPlayer.toLowerCase();
      const queryWords = nameQuery.toLowerCase().split(/\s+/);
      const nameMatch = queryWords.some(word => playerName.includes(word)) || playerName.includes(nameQuery.toLowerCase());
      
      if (!nameMatch) {
        continue;
      }
    }

    // Map and validate position
    const mappedPosition = mapTheSportsDBPosition(player.strPosition);
    if (!isSoccerPosition(mappedPosition, player.strPosition)) {
      continue;
    }

    // Position filter (if specified)
    if (positionFilter && mappedPosition !== positionFilter) {
      continue;
    }

    // Create player object
    const playerName = decodeHtmlEntities(player.strPlayer || 'Unknown Player');
    const imageUrl = player.strCutout || player.strThumb || generatePlayerInitialsImage(playerName);
    
    // Use team info if provided, otherwise fall back to player data
    const teamName = teamInfo?.teamName || player.strTeam || '';
    const country = teamInfo?.teamCountry || player.strNationality || player.strBirthLocation || '';

    players.push({
      id: player.idPlayer || `sportsdb-${Date.now()}-${Math.random()}`,
      name: playerName,
      position: mappedPosition,
      imageUrl: imageUrl,
      country: country,
      year: player.strBirthLocation ? parseInt(player.strBirthLocation, 10) : undefined,
      team: teamName
    });
  }

  console.log(`🔄 Parsed ${players.length} soccer players from ${playerData.length} total players`);
  return players;
};

// Apply advanced filters to player list
const applyAdvancedFilters = (players: ApiPlayer[], filters: AdvancedSearchFilters): ApiPlayer[] => {
  return players.filter(player => {
    // Year filter
    if (filters.year && player.year) {
      const yearDiff = Math.abs(player.year - filters.year);
      if (yearDiff > 10) return false; // Allow 10-year range
    }

    // Smart country filtering logic:
    // - If ONLY country is selected: show players from that country
    // - If country + league/team is selected: show all players from that league/team regardless of nationality
    //   (because international players are common - e.g., Mbappé is French but plays in Spanish La Liga)
    if (filters.country && player.country) {
      const hasLeagueOrTeamFilter = filters.league || filters.team;
      
      if (!hasLeagueOrTeamFilter) {
        // Country filter only - show players from that country
        const playerCountry = player.country.toLowerCase();
        const targetCountry = filters.country.toLowerCase();
        if (!playerCountry.includes(targetCountry) && !targetCountry.includes(playerCountry)) {
          return false;
        }
      }
      // If league/team filter is also present, we'll rely on those filters instead of country
      // This allows international players (like Mbappé in Real Madrid) to show up
    }

    // Team filter - this should work regardless of player nationality
    if (filters.team && player.team) {
      const playerTeam = player.team.toLowerCase();
      const targetTeam = filters.team.toLowerCase();
      if (!playerTeam.includes(targetTeam) && !targetTeam.includes(playerTeam)) {
        return false;
      }
    }

    return true;
  });
};

// Function to get player image (unchanged)
export const getPlayerImage = async (playerId: string, playerName: string): Promise<string> => {
  const cachedImage = getCachedImage(playerId, playerName);
  if (cachedImage) {
    return cachedImage;
  }

  try {
    const timeoutMs = 3000;
    
    const sportsDBResponse = await axios.get(
      PLAYER_IMAGE_API.BASE_URL,
      {
        params: { s: playerName },
        timeout: timeoutMs
      }
    );
    
    interface SportDBResponse {
      player?: Array<{ strThumb?: string; strCutout?: string }>;
    }
    
    const sportDBData = sportsDBResponse.data as SportDBResponse;
    
    if (sportDBData.player?.[0]?.strCutout) {
      const imageUrl = sportDBData.player[0].strCutout;
      cacheImage(playerId, playerName, imageUrl);
      return imageUrl;
    } else if (sportDBData.player?.[0]?.strThumb) {
      const imageUrl = sportDBData.player[0].strThumb;
      cacheImage(playerId, playerName, imageUrl);
      return imageUrl;
    }
    
    const initials = playerName
      .split(' ')
      .map(name => name[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
      
    return `https://via.placeholder.com/150/4CAF50/FFFFFF?text=${encodeURIComponent(initials)}`;
  } catch (error) {
    console.error('Error fetching player image:', error);
    
    const initials = playerName
      .split(' ')
      .map(name => name[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
    
    return `https://via.placeholder.com/150/4CAF50/FFFFFF?text=${encodeURIComponent(initials)}`;
  }
};

// Helper function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

// Helper function to generate a player image with initials
const generatePlayerInitialsImage = (playerName: string): string => {
  const canvas = document.createElement('canvas');
  canvas.width = 150;
  canvas.height = 150;
  const ctx = canvas.getContext('2d');
  
  const initials = playerName
    .split(' ')
    .map(name => name[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  
  if (ctx) {
    ctx.fillStyle = '#4CAF50';
    ctx.beginPath();
    ctx.arc(75, 75, 75, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.fillStyle = 'white';
    ctx.font = 'bold 60px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, 75, 75);
  }
  
  return canvas.toDataURL();
};

// Helper function to check if a position is a valid soccer position
const isSoccerPosition = (mappedPosition: Position, originalPosition?: string): boolean => {
  const validSoccerPositions: Position[] = [
    'GK', 'LB', 'CB', 'RB', 'LWB', 'RWB', 
    'CDM', 'CM', 'CAM', 'LM', 'RM', 
    'LW', 'RW', 'CF', 'ST'
  ];
  
  if (!validSoccerPositions.includes(mappedPosition)) {
    return false;
  }
  
  if (originalPosition) {
    const pos = originalPosition.toLowerCase();
    const nonSoccerTerms = [
      'quarterback', 'pitcher', 'catcher', 'point guard', 'center', 'power forward',
      'small forward', 'shooting guard', 'tight end', 'running back', 'wide receiver',
      'linebacker', 'cornerback', 'safety', 'offensive line', 'defensive line'
    ];
    
    for (const term of nonSoccerTerms) {
      if (pos.includes(term)) {
        return false;
      }
    }
  }
  
  return true;
};

// Helper function to map TheSportsDB positions to our position format
const mapTheSportsDBPosition = (position?: string): Position => {
  if (!position) return 'CM';
  
  const pos = position.toLowerCase();
  
  // Goalkeeper
  if (pos.includes('goalkeeper') || pos === 'gk' || pos.includes('keeper')) return 'GK';
  
  // Defenders
  if (pos.includes('centre-back') || pos.includes('center back') || pos.includes('central defender')) return 'CB';
  if (pos.includes('right back') || pos === 'rb' || pos.includes('right-back')) return 'RB';
  if (pos.includes('left back') || pos === 'lb' || pos.includes('left-back')) return 'LB';
  if (pos.includes('right wing back') || pos === 'rwb') return 'RWB';
  if (pos.includes('left wing back') || pos === 'lwb') return 'LWB';
  if (pos.includes('defender') && !pos.includes('midfield')) return 'CB';
  
  // Midfielders
  if (pos.includes('defensive midfielder') || pos === 'cdm' || pos.includes('holding midfielder')) return 'CDM';
  if (pos.includes('attacking midfielder') || pos === 'cam' || pos.includes('playmaker')) return 'CAM';
  if (pos.includes('right midfielder') || pos === 'rm' || pos.includes('right midfield')) return 'RM';
  if (pos.includes('left midfielder') || pos === 'lm' || pos.includes('left midfield')) return 'LM';
  if (pos.includes('central midfielder') || pos === 'cm' || pos.includes('midfielder')) return 'CM';
  
  // Wingers
  if (pos.includes('right winger') || pos === 'rw' || pos.includes('right wing')) return 'RW';
  if (pos.includes('left winger') || pos === 'lw' || pos.includes('left wing')) return 'LW';
  
  // Forwards
  if (pos.includes('striker') || pos === 'st' || pos.includes('centre forward')) return 'ST';
  if (pos.includes('forward') || pos === 'cf') return 'CF';
  
  // Default case
  return 'CM';
};

// Helper function to sort players by position priority
const sortPlayersByPositionPriority = (players: ApiPlayer[], searchPosition?: Position): ApiPlayer[] => {
  const isStrikerSearch = searchPosition === 'ST' || searchPosition === 'CF';
  
  return [...players].sort((a, b) => {
    if (isStrikerSearch) {
      const aIsStriker = a.position === 'ST' || a.position === 'CF';
      const bIsStriker = b.position === 'ST' || b.position === 'CF';
      
      if (aIsStriker && !bIsStriker) return -1;
      if (!aIsStriker && bIsStriker) return 1;
    } else if (searchPosition) {
      const aMatchesSearch = a.position === searchPosition;
      const bMatchesSearch = b.position === searchPosition;
      
      if (aMatchesSearch && !bMatchesSearch) return -1;
      if (!aMatchesSearch && bMatchesSearch) return 1;
    }
    
    return a.name.localeCompare(b.name);
  });
};

// Mock function to simulate search results (soccer players only)
const mockSearchResults = (
  query: string, 
  position?: Position,
  advancedFilters?: AdvancedSearchFilters
): ApiPlayer[] => {
  // Comprehensive list of famous soccer players
  const allPlayers: ApiPlayer[] = [
    { id: '1', name: 'Lionel Messi', position: 'RW', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=LM', country: 'Argentina', team: 'Inter Miami', year: 1987 },
    { id: '2', name: 'Cristiano Ronaldo', position: 'ST', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=CR', country: 'Portugal', team: 'Al Nassr', year: 1985 },
    { id: '3', name: 'Kylian Mbappé', position: 'ST', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=KM', country: 'France', team: 'Real Madrid', year: 1998 },
    { id: '4', name: 'Erling Haaland', position: 'ST', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=EH', country: 'Norway', team: 'Manchester City', year: 2000 },
    { id: '5', name: 'Vinicius Junior', position: 'LW', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=VJ', country: 'Brazil', team: 'Real Madrid', year: 2000 },
    { id: '6', name: 'Kevin De Bruyne', position: 'CM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=KD', country: 'Belgium', team: 'Manchester City', year: 1991 },
    { id: '7', name: 'Virgil van Dijk', position: 'CB', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=VD', country: 'Netherlands', team: 'Liverpool', year: 1991 },
    { id: '8', name: 'Mohamed Salah', position: 'RW', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=MS', country: 'Egypt', team: 'Liverpool', year: 1992 },
    { id: '9', name: 'Robert Lewandowski', position: 'ST', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=RL', country: 'Poland', team: 'Barcelona', year: 1988 },
    { id: '10', name: 'Luka Modric', position: 'CM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=LM', country: 'Croatia', team: 'Real Madrid', year: 1985 },
    
    // International players in Spanish La Liga
    { id: '16', name: 'Jude Bellingham', position: 'CM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=JB', country: 'England', team: 'Real Madrid', year: 2003 },
    { id: '17', name: 'Frenkie de Jong', position: 'CM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=FJ', country: 'Netherlands', team: 'Barcelona', year: 1997 },
    { id: '18', name: 'Pedri', position: 'CAM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=PD', country: 'Spain', team: 'Barcelona', year: 2002 },
    { id: '19', name: 'Gavi', position: 'CM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=GV', country: 'Spain', team: 'Barcelona', year: 2004 },
    
    // International players in Premier League
    { id: '20', name: 'Bruno Fernandes', position: 'CAM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=BF', country: 'Portugal', team: 'Manchester United', year: 1994 },
    { id: '21', name: 'Son Heung-min', position: 'LW', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=SH', country: 'South Korea', team: 'Tottenham', year: 1992 },
    { id: '22', name: 'Bukayo Saka', position: 'RW', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=BS', country: 'England', team: 'Arsenal', year: 2001 },
    
    // Add legendary players
    { id: '11', name: 'Pele', position: 'CF', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=PE', country: 'Brazil', year: 1940, team: 'Santos' },
    { id: '12', name: 'Diego Maradona', position: 'CAM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=DM', country: 'Argentina', year: 1960, team: 'Napoli' },
    { id: '13', name: 'Johan Cruyff', position: 'CF', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=JC', country: 'Netherlands', year: 1947, team: 'Ajax' },
    { id: '14', name: 'Zinedine Zidane', position: 'CAM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=ZZ', country: 'France', year: 1972, team: 'Real Madrid' },
    { id: '15', name: 'Ronaldinho', position: 'CAM', imageUrl: 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=RO', country: 'Brazil', year: 1980, team: 'Barcelona' },
  ];

  const cleanQuery = query.toLowerCase().trim();
  
  if (cleanQuery === '' && !position && !advancedFilters?.country && !advancedFilters?.league && !advancedFilters?.year && !advancedFilters?.team) {
    return allPlayers;
  }

  const queryWords = cleanQuery.split(/\s+/);
  
  let filteredPlayers = allPlayers.filter(player => {
    // Name filter
    if (cleanQuery) {
      const playerName = player.name.toLowerCase();
      const nameMatch = playerName.includes(cleanQuery) || 
        queryWords.some(word => playerName.includes(word));
      if (!nameMatch) return false;
    }
    
    // Position filter
    if (position && player.position !== position) return false;
    
    // Advanced filters
    if (advancedFilters) {
      // Year filter
      if (advancedFilters.year && player.year) {
        const yearMatch = Math.abs(player.year - advancedFilters.year) <= 10;
        if (!yearMatch) return false;
      }
      
      // Smart country filtering (same logic as API filtering)
      if (advancedFilters.country && player.country) {
        const hasLeagueOrTeamFilter = advancedFilters.league || advancedFilters.team;
        
        if (!hasLeagueOrTeamFilter) {
          // Country filter only - show players from that country
          const playerCountry = player.country.toLowerCase();
          const targetCountry = advancedFilters.country.toLowerCase();
          const countryMatch = playerCountry.includes(targetCountry) || targetCountry.includes(playerCountry);
          if (!countryMatch) return false;
        }
        // If league/team filter is also present, ignore country filter to allow international players
      }
      
      // League filter (for mock data, check against team names for major leagues)
      if (advancedFilters.league && player.team) {
        const playerTeam = player.team.toLowerCase();
        const targetLeague = advancedFilters.league.toLowerCase();
        
        // Map team names to leagues for mock data filtering
        const leagueTeamMapping: {[key: string]: string[]} = {
          'premier-league': ['arsenal', 'chelsea', 'liverpool', 'manchester city', 'manchester united', 'tottenham'],
          'la-liga': ['real madrid', 'barcelona', 'atletico madrid', 'sevilla'],
          'serie-a': ['juventus', 'ac milan', 'inter milan', 'napoli'],
          'bundesliga': ['bayern munich', 'borussia dortmund'],
          'ligue-1': ['paris saint-germain', 'marseille', 'lyon'],
          'mls': ['inter miami', 'la galaxy'],
          // Additional mappings to match our data structure
          'english premier league': ['arsenal', 'chelsea', 'liverpool', 'manchester city', 'manchester united', 'tottenham'],
          'spanish la liga': ['real madrid', 'barcelona', 'atletico madrid', 'sevilla'],
          'italian serie a': ['juventus', 'ac milan', 'inter milan', 'napoli'],
          'german bundesliga': ['bayern munich', 'borussia dortmund'],
          'french ligue 1': ['paris saint-germain', 'marseille', 'lyon']
        };
        
        const leagueMatch = leagueTeamMapping[targetLeague]?.some(teamName => 
          playerTeam.includes(teamName)
        ) || playerTeam.includes(targetLeague);
        
        if (!leagueMatch) return false;
      }
      
      // Team filter - regardless of player nationality
      if (advancedFilters.team && player.team) {
        const playerTeam = player.team.toLowerCase();
        const targetTeam = advancedFilters.team.toLowerCase();
        const teamMatch = playerTeam.includes(targetTeam) || targetTeam.includes(playerTeam);
        if (!teamMatch) return false;
      }
    }
    
    return true;
  });

  console.log(`📝 Mock search results for "${query}": ${filteredPlayers.length} players`);
  return sortPlayersByPositionPriority(filteredPlayers, position);
}; 